// client/src/components/pages/RouteIntelligence/ReceiptReconDetailModal.tsx
// (converted from .js, ST-00218 P4: typed; behaviour unchanged apart from the two Truck + time
// statuses below)
//
// On a "Truck + time" route the Receipt Recon monitor pairs loads from the load-match ledger
// and stores two things the delivery-number Recon never did:
//   * status `NoReceiptData` — a dispatch of a day the route had no receipt data for: not
//     matched, not missing, not in transit. Shown (and exported) as "No receipt data", with its
//     own filter option;
//   * match type `needs_review` on a Missing dispatch — the truck was dispatched again before any
//     receipt (a double dispatch to check, not necessarily a lost load). Tagged "Needs review"
//     next to the Missing badge; the match column (and the export) say "Double dispatch".
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X,
  Mail,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  List,
  LayoutGrid,
  Download,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import type { UserOptions } from 'jspdf-autotable';
import api from '../../../services/api';
import {
  getReconState,
  formatDate,
  formatDateShort,
  formatMatchRate,
  formatWeight,
  formatHours,
  DAY_RANGES,
} from './receiptReconConfig';
import ReceiptReconAcknowledgeModal from './ReceiptReconAcknowledgeModal';
import ReceiptReconAlertLogModal from './ReceiptReconAlertLogModal';
import ThemedSelect from '../../common/ThemedSelect';
import { toApiDateString } from '../../../utils/dateUtils';
import { useThemeTokens } from '../../../utils/themeTokens';
import type {
  ReceiptReconAcknowledgment,
  ReceiptReconDashboardMonitor,
  ReceiptReconResultRow,
  ReceiptReconResultSummary,
  ReceiptReconTicketRow,
} from '../../../types/clientPages/routeIntelligence';

/** The `jspdf-autotable` side-effect import adds `doc.autoTable(...)` (not in jsPDF's types). */
type AutoTableDoc = jsPDF & { autoTable: (options: UserOptions) => void };

/** One bar of the match-rate chart. */
interface ChartPoint {
  date: string;
  matchRate: number;
  unmatchedDispatches: number;
  totalDispatches: number;
  hasIssues: boolean;
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: ChartPoint }>;
}

/** The ticket statuses in words where the stored code is not one (ST-00218 P4). */
const STATUS_LABELS: Record<string, string> = { NoReceiptData: 'No receipt data' };
const statusLabel = (status: string): string => STATUS_LABELS[status] ?? status;

/** Badge tint per status (theme tint classes: they flip in dark mode). */
const statusClass = (status: string): string =>
  status === 'Missing'
    ? 'bg-red-50 text-red-600'
    : status === 'InTransit'
      ? 'bg-blue-50 text-blue-600'
      : status === 'Orphan'
        ? 'bg-amber-50 text-amber-600'
        : status === 'WeightAlert'
          ? 'bg-orange-50 text-orange-600'
          : status === 'NoReceiptData'
            ? 'bg-slate-100 text-slate-700'
            : 'bg-purple-50 text-purple-600';

/** A Missing dispatch the truck was dispatched again after before any receipt (ledger). */
const isNeedsReview = (t: ReceiptReconTicketRow): boolean => t.match_type === 'needs_review';

const NEEDS_REVIEW_TITLE =
  'The truck was dispatched again before a receipt for this load. Check which dispatch is real.';

/** The match type as the table shows it ('Double dispatch' for a needs-review row). */
const matchTypeText = (t: ReceiptReconTicketRow): string =>
  isNeedsReview(t) ? 'Double dispatch' : t.match_type || '';

/** A ticket's status badge, plus the "Needs review" tag on a double dispatch. */
const StatusCell = ({ ticket }: { ticket: ReceiptReconTicketRow }) => (
  <span className="inline-flex flex-wrap items-center gap-1">
    <span
      className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-semibold ${statusClass(ticket.status)}`}
    >
      {statusLabel(ticket.status)}
    </span>
    {isNeedsReview(ticket) && (
      <span
        className="inline-flex px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-[9px] font-semibold text-amber-800"
        title={NEEDS_REVIEW_TITLE}
      >
        Needs review
      </span>
    )}
  </span>
);

const ChartTooltip = ({ active, payload }: ChartTooltipProps) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]!.payload;
  return (
    <div className="bg-surface border border-gray-200 rounded-md px-3 py-2 shadow-lg text-[11px]">
      <div className="text-slate-800 font-medium mb-1">{formatDate(d.date)}</div>
      <div className="flex items-center gap-2">
        <span
          className={`font-bold ${d.matchRate >= 100 ? 'text-primary-500' : d.matchRate >= 90 ? 'text-amber-600' : 'text-red-600'}`}
        >
          {d.matchRate}% match
        </span>
        <span className="text-gray-300">|</span>
        <span className="text-gray-400">{d.unmatchedDispatches} missing</span>
        <span className="text-gray-300">|</span>
        <span className="text-gray-400">{d.totalDispatches} dispatches</span>
      </div>
    </div>
  );
};

interface ReceiptReconDetailModalProps {
  monitor: ReceiptReconDashboardMonitor;
  onClose: () => void;
  onRefresh: () => void;
}

const ReceiptReconDetailModal = ({ monitor, onClose, onRefresh }: ReceiptReconDetailModalProps) => {
  const t = useThemeTokens();
  const [results, setResults] = useState<ReceiptReconResultRow[]>([]);
  const [summary, setSummary] = useState<ReceiptReconResultSummary | null>(null);
  const [myAcks, setMyAcks] = useState<Record<string, unknown>>({});
  const [allAcks, setAllAcks] = useState<Record<string, ReceiptReconAcknowledgment[]>>({});
  const [loading, setLoading] = useState(true);
  const [showAckModal, setShowAckModal] = useState<ReceiptReconResultRow | null>(null);
  const [showAlertLog, setShowAlertLog] = useState(false);
  const [dayRange, setDayRange] = useState(7);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [ticketData, setTicketData] = useState<Record<string, ReceiptReconTicketRow[]>>({});
  const [viewMode, setViewMode] = useState<'daily' | 'tickets'>('daily');
  const [allTickets, setAllTickets] = useState<ReceiptReconTicketRow[] | null>(null);
  const [loadingAllTickets, setLoadingAllTickets] = useState(false);
  const [ticketFilter, setTicketFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [r, s, a] = await Promise.all([
        api.receiptRecon.getResults(monitor.monitor_id, monitor.lookback_days || 40),
        api.receiptRecon.getResultSummary(monitor.monitor_id),
        api.receiptRecon.getMyAcknowledgments(monitor.monitor_id),
      ]);
      setResults((r.data as ReceiptReconResultRow[]) || []);
      setSummary((s.data as ReceiptReconResultSummary) || {});
      setMyAcks((a.data as { ackMap?: Record<string, unknown> } | null)?.ackMap || {});
    } catch {
    } finally {
      setLoading(false);
    }
  }, [monitor.monitor_id, monitor.lookback_days]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fn = async () => {
      const withIssues = results.filter((r) => r.unmatched_dispatches > 0 || r.orphan_receipts > 0);
      const data: Record<string, ReceiptReconAcknowledgment[]> = {};
      await Promise.all(
        withIssues.map(async (r) => {
          try {
            const res = await api.receiptRecon.getAcknowledgments(r.result_id);
            data[r.result_id] = (res.data as ReceiptReconAcknowledgment[]) || [];
          } catch {
            data[r.result_id] = [];
          }
        })
      );
      setAllAcks(data);
    };
    if (results.length > 0) fn();
  }, [results]);

  useEffect(() => {
    if (viewMode === 'tickets' && allTickets === null && !loadingAllTickets) {
      setLoadingAllTickets(true);
      api.receiptRecon
        .getMonitorTickets(monitor.monitor_id, monitor.lookback_days || 40)
        .then((res) => setAllTickets((res.data as ReceiptReconTicketRow[]) || []))
        .catch(() => setAllTickets([]))
        .finally(() => setLoadingAllTickets(false));
    }
  }, [viewMode, allTickets, loadingAllTickets, monitor.monitor_id, monitor.lookback_days]);

  const handleAckSaved = () => {
    setShowAckModal(null);
    fetchData();
    onRefresh();
  };

  const toggleRow = async (resultId: number) => {
    const next = new Set(expandedRows);
    if (next.has(resultId)) {
      next.delete(resultId);
    } else {
      next.add(resultId);
      if (!ticketData[resultId]) {
        try {
          const res = await api.receiptRecon.getTickets(resultId);
          setTicketData((prev) => ({
            ...prev,
            [resultId]: (res.data as ReceiptReconTicketRow[]) || [],
          }));
        } catch {
          setTicketData((prev) => ({ ...prev, [resultId]: [] }));
        }
      }
    }
    setExpandedRows(next);
  };

  const filteredResults = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - dayRange);
    return results.filter((r) => new Date(r.result_date) >= cutoff);
  }, [results, dayRange]);

  const chartData = useMemo((): ChartPoint[] => {
    return [...filteredResults].reverse().map((r) => ({
      date: r.result_date,
      matchRate: parseFloat(String(r.match_rate)) || 0,
      unmatchedDispatches: r.unmatched_dispatches || 0,
      totalDispatches: r.total_dispatches || 0,
      hasIssues: r.unmatched_dispatches > 0 || r.orphan_receipts > 0,
    }));
  }, [filteredResults]);

  const filteredTickets = useMemo(() => {
    if (!allTickets) return [];
    if (ticketFilter === 'all') return allTickets;
    return allTickets.filter((t) => t.status === ticketFilter);
  }, [allTickets, ticketFilter]);

  const handleExportExcel = async () => {
    setExporting(true);
    setShowExportMenu(false);
    try {
      const wb = new ExcelJS.Workbook();

      // Sheet 1: Daily summary
      const ws1 = wb.addWorksheet('Daily Summary');
      ws1.columns = [
        { header: 'Date', key: 'date', width: 14 },
        { header: 'Dispatches', key: 'dispatches', width: 12 },
        { header: 'Receipts', key: 'receipts', width: 12 },
        { header: 'Matched', key: 'matched', width: 10 },
        { header: 'Missing', key: 'missing', width: 10 },
        { header: 'Orphans', key: 'orphans', width: 10 },
        { header: 'In Transit', key: 'transit', width: 10 },
        { header: 'Match Rate', key: 'rate', width: 12 },
        { header: 'Wt Alerts', key: 'weightAlerts', width: 10 },
      ];
      ws1.getRow(1).font = { bold: true };
      filteredResults.forEach((r) => {
        const row = ws1.addRow({
          date: new Date(r.result_date).toLocaleDateString('en-ZA'),
          dispatches: r.total_dispatches,
          receipts: r.total_receipts,
          matched: r.matched_count,
          missing: r.unmatched_dispatches,
          orphans: r.orphan_receipts,
          transit: r.in_transit_count,
          rate: `${r.match_rate}%`,
          weightAlerts: r.weight_alerts,
        });
        if (r.unmatched_dispatches > 0)
          row.getCell('missing').font = { color: { argb: 'FFDC2626' }, bold: true };
      });

      // Sheet 2: All tickets
      if (allTickets && allTickets.length > 0) {
        const ws2 = wb.addWorksheet('Tickets');
        ws2.columns = [
          { header: 'Date', key: 'date', width: 14 },
          { header: 'Status', key: 'status', width: 14 },
          { header: 'Ticket No', key: 'ticketNo', width: 18 },
          { header: 'Reg No', key: 'regNo', width: 14 },
          { header: 'Timestamp', key: 'timestamp', width: 18 },
          { header: 'Weight', key: 'weight', width: 12 },
          { header: 'Product', key: 'product', width: 16 },
          { header: 'Match Type', key: 'matchType', width: 12 },
          { header: 'Matched Reg', key: 'matchedReg', width: 14 },
          { header: 'Wt Variance %', key: 'wtVar', width: 14 },
          { header: 'Hours', key: 'hours', width: 8 },
        ];
        ws2.getRow(1).font = { bold: true };
        allTickets.forEach((t) => {
          ws2.addRow({
            date: t.result_date ? new Date(t.result_date).toLocaleDateString('en-ZA') : '',
            status: statusLabel(t.status),
            ticketNo: t.ticket_no || t.delivery_no || '',
            regNo: t.reg_no || '',
            timestamp: t.ticket_timestamp
              ? new Date(t.ticket_timestamp).toLocaleString('en-ZA')
              : '',
            weight: t.nett_weight ? parseFloat(String(t.nett_weight)).toFixed(1) : '',
            product: t.product || '',
            matchType: isNeedsReview(t) ? 'Double dispatch (needs review)' : t.match_type || '',
            matchedReg: t.matched_reg_no || '',
            wtVar: t.weight_variance_percent
              ? `${parseFloat(String(t.weight_variance_percent)).toFixed(2)}%`
              : '',
            hours: t.hours_since_dispatch || '',
          });
        });
      }

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Receipt-Recon-${monitor.grouping_name}-${toApiDateString(new Date())}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    setShowExportMenu(false);
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' }) as AutoTableDoc;
      doc.setFontSize(16);
      doc.text(`Receipt Reconciliation: ${monitor.grouping_name}`, 14, 15);
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.text(
        `Generated: ${new Date().toLocaleString('en-ZA')} | Grace: ${monitor.grace_window_hours}h | Threshold: ${monitor.missing_threshold} missing`,
        14,
        22
      );

      doc.autoTable({
        startY: 28,
        head: [
          [
            'Date',
            'Dispatches',
            'Matched',
            'Missing',
            'Orphans',
            'Transit',
            'Match Rate',
            'Wt Alerts',
          ],
        ],
        body: filteredResults.map((r) => [
          new Date(r.result_date).toLocaleDateString('en-ZA'),
          r.total_dispatches,
          r.matched_count,
          r.unmatched_dispatches,
          r.orphan_receipts,
          r.in_transit_count,
          `${r.match_rate}%`,
          r.weight_alerts,
        ]),
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [45, 49, 66] },
      });

      doc.save(`Receipt-Recon-${monitor.grouping_name}-${toApiDateString(new Date())}.pdf`);
    } catch {
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <div className="bg-surface rounded-lg p-10">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-primary-500 mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg border border-gray-200 w-full max-w-5xl max-h-[92vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">{monitor.grouping_name}</h2>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Receipt Reconciliation Details | Grace: {monitor.grace_window_hours}h | Alert at{' '}
              {monitor.missing_threshold} missing
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAlertLog(true)}
              className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-medium text-gray-500 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md"
            >
              <Mail className="w-3 h-3" /> Alert Log
            </button>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-6 gap-px bg-gray-100 border-b border-gray-100 flex-shrink-0">
          {[
            {
              label: 'Match Rate',
              value: formatMatchRate(monitor.latest_match_rate),
              color:
                Number(monitor.latest_match_rate) >= 100
                  ? 'var(--accent)'
                  : Number(monitor.latest_match_rate) >= 90
                    ? 'var(--warning)'
                    : 'var(--danger)',
            },
            {
              label: 'Missing',
              value: `${monitor.latest_unmatched || 0}`,
              color: (monitor.latest_unmatched || 0) > 0 ? 'var(--danger)' : 'var(--accent)',
            },
            {
              label: 'Orphans',
              value: `${monitor.latest_orphan_receipts || 0}`,
              color: 'var(--text-strong)',
            },
            {
              label: 'In Transit',
              value: `${monitor.latest_in_transit || 0}`,
              color: 'var(--text-strong)',
            },
            {
              label: 'Wt Alerts',
              value: `${monitor.latest_weight_alerts || 0}`,
              color:
                (monitor.latest_weight_alerts || 0) > 0 ? 'var(--warning)' : 'var(--text-strong)',
            },
            {
              label: '30d Missing',
              value: `${summary?.totalMissingIn30 || 0}`,
              color: 'var(--text-muted)',
            },
          ].map((kpi, i) => (
            <div key={i} className="bg-surface px-3 py-2.5 text-center">
              <div className="text-sm font-bold tabular-nums" style={{ color: kpi.color }}>
                {kpi.value}
              </div>
              <div className="text-[9px] text-gray-400 uppercase tracking-wider mt-0.5">
                {kpi.label}
              </div>
            </div>
          ))}
        </div>

        <div className="flex-1 overflow-auto px-5 py-4">
          {/* Chart */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-slate-800">Match Rate Trend</h3>
              <div className="flex items-center gap-1">
                {DAY_RANGES.map((r) => (
                  <button
                    key={r.value}
                    onClick={() => setDayRange(r.value)}
                    className={`px-2 py-0.5 text-[10px] rounded-md font-medium ${dayRange === r.value ? 'bg-primary-500/10 text-primary-700 ring-1 ring-inset ring-primary-500/70' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={t.gridSubtle} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d: string) => formatDateShort(d)}
                    tick={{ fontSize: 10, fill: t.axisLabel }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 10, fill: t.axisLabel }}
                    tickFormatter={(v: number) => `${v}%`}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <ReferenceLine y={100} stroke={t.accent} strokeDasharray="4 4" strokeWidth={1} />
                  <Bar dataKey="matchRate" radius={[2, 2, 0, 0]} maxBarSize={24}>
                    {chartData.map((entry, index) => (
                      <Cell
                        key={index}
                        fill={
                          entry.matchRate >= 100
                            ? t.accent
                            : entry.matchRate >= 90
                              ? t.warning
                              : t.danger
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-32 flex items-center justify-center text-xs text-gray-300">
                No data in range
              </div>
            )}
          </div>

          {/* View mode + export */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('daily')}
                className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium rounded-md transition-all ${viewMode === 'daily' ? 'bg-surface shadow text-slate-800' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <LayoutGrid className="w-3 h-3" /> Daily
              </button>
              <button
                onClick={() => setViewMode('tickets')}
                className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium rounded-md transition-all ${viewMode === 'tickets' ? 'bg-surface shadow text-slate-800' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <List className="w-3 h-3" /> All Tickets
              </button>
            </div>
            <div className="flex items-center gap-2">
              {viewMode === 'tickets' && (
                <ThemedSelect
                  value={ticketFilter}
                  onChange={(e) => setTicketFilter(String(e.target.value))}
                  className="h-7 pl-2 pr-6 text-[10px] bg-surface border border-gray-200 rounded-md text-gray-600 focus:outline-none appearance-none"
                >
                  <option value="all">All Types</option>
                  <option value="Missing">Missing</option>
                  <option value="InTransit">In Transit</option>
                  <option value="NoReceiptData">No receipt data</option>
                  <option value="Orphan">Orphan</option>
                  <option value="WeightAlert">Weight Alert</option>
                  <option value="RegNoMismatch">Reg Mismatch</option>
                </ThemedSelect>
              )}
              <div className="relative">
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  disabled={exporting}
                  className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-medium text-gray-500 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md disabled:opacity-50"
                >
                  <Download className="w-3 h-3" /> Export
                </button>
                {showExportMenu && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowExportMenu(false)} />
                    <div className="absolute right-0 top-full mt-1 w-32 bg-surface rounded-lg shadow-lg border border-gray-200 z-20 py-1">
                      <button
                        onClick={handleExportExcel}
                        className="w-full px-3 py-1.5 text-left text-xs text-gray-600 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <FileSpreadsheet className="w-3 h-3" /> Excel
                      </button>
                      <button
                        onClick={handleExportPDF}
                        className="w-full px-3 py-1.5 text-left text-xs text-gray-600 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <FileText className="w-3 h-3" /> PDF
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Daily View */}
          {viewMode === 'daily' && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="w-8 px-2 py-2" />
                    {[
                      'Date',
                      'Dispatches',
                      'Matched',
                      'Missing',
                      'Orphans',
                      'Transit',
                      'Rate',
                      'Wt',
                      'Ack',
                      '',
                    ].map((h, i) => (
                      <th
                        key={h}
                        className={`px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider ${i >= 7 ? 'text-center' : 'text-left'}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredResults.map((r) => {
                    const isExpanded = expandedRows.has(r.result_id);
                    const hasIssues = r.unmatched_dispatches > 0 || r.orphan_receipts > 0;
                    const rState = getReconState(r.unmatched_dispatches, monitor.missing_threshold);
                    const acks = allAcks[r.result_id] || [];
                    const myAck = myAcks[r.result_id];
                    return (
                      <React.Fragment key={r.result_id}>
                        <tr
                          className={`border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer transition-colors ${rState.rowBg}`}
                          onClick={() =>
                            (hasIssues || r.weight_alerts > 0) && toggleRow(r.result_id)
                          }
                        >
                          <td className="px-2 py-2 text-gray-300">
                            {(hasIssues || r.weight_alerts > 0) &&
                              (isExpanded ? (
                                <ChevronDown className="w-3.5 h-3.5" />
                              ) : (
                                <ChevronRight className="w-3.5 h-3.5" />
                              ))}
                          </td>
                          <td className="px-2 py-2 text-slate-800 font-medium whitespace-nowrap">
                            {formatDate(r.result_date)}
                          </td>
                          <td className="px-2 py-2 tabular-nums">{r.total_dispatches}</td>
                          <td className="px-2 py-2 tabular-nums text-primary-500 font-medium">
                            {r.matched_count}
                          </td>
                          <td className="px-2 py-2 tabular-nums">
                            <span
                              className={
                                r.unmatched_dispatches > 0
                                  ? 'text-red-600 font-bold'
                                  : 'text-gray-300'
                              }
                            >
                              {r.unmatched_dispatches}
                            </span>
                          </td>
                          <td className="px-2 py-2 tabular-nums">
                            <span
                              className={
                                r.orphan_receipts > 0
                                  ? 'text-amber-600 font-medium'
                                  : 'text-gray-300'
                              }
                            >
                              {r.orphan_receipts}
                            </span>
                          </td>
                          <td className="px-2 py-2 tabular-nums text-gray-400">
                            {r.in_transit_count}
                          </td>
                          <td
                            className="px-2 py-2 tabular-nums font-medium"
                            style={{
                              color:
                                Number(r.match_rate) >= 100
                                  ? 'var(--accent)'
                                  : Number(r.match_rate) >= 90
                                    ? 'var(--warning)'
                                    : 'var(--danger)',
                            }}
                          >
                            {r.match_rate}%
                          </td>
                          <td className="px-2 py-2 text-center tabular-nums">
                            <span
                              className={
                                r.weight_alerts > 0 ? 'text-amber-600 font-medium' : 'text-gray-300'
                              }
                            >
                              {r.weight_alerts}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-center">
                            {acks.length > 0 ? (
                              <span
                                className="inline-flex items-center gap-0.5 text-primary-500 text-[10px]"
                                title={acks.map((a) => a.user_name).join(', ')}
                              >
                                <CheckCircle className="w-3 h-3" /> {acks.length}
                              </span>
                            ) : hasIssues ? (
                              myAck ? (
                                <CheckCircle className="w-3 h-3 text-primary-500 mx-auto" />
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowAckModal(r);
                                  }}
                                  className="text-[10px] text-gray-400 hover:text-primary-500 underline"
                                >
                                  Ack
                                </button>
                              )
                            ) : null}
                          </td>
                          <td className="px-2 py-2" />
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={11} className="bg-gray-50/50 px-6 py-3">
                              {!ticketData[r.result_id] ? (
                                <div className="text-center py-3">
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-200 border-t-primary-500 mx-auto" />
                                </div>
                              ) : ticketData[r.result_id]!.length === 0 ? (
                                <div className="text-center py-2 text-[11px] text-gray-400">
                                  No ticket details
                                </div>
                              ) : (
                                <table className="w-full text-[11px]">
                                  <thead>
                                    <tr className="text-[9px] text-gray-400 uppercase tracking-wider border-b border-gray-200">
                                      <th className="pb-1.5 text-left">Status</th>
                                      <th className="pb-1.5 text-left">Ticket No</th>
                                      <th className="pb-1.5 text-left">Reg No</th>
                                      <th className="pb-1.5 text-left">Timestamp</th>
                                      <th className="pb-1.5 text-right">Weight</th>
                                      <th className="pb-1.5 text-left">Product</th>
                                      <th className="pb-1.5 text-left">Match</th>
                                      <th className="pb-1.5 text-right">Wt Var %</th>
                                      <th className="pb-1.5 text-right">Hours</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {ticketData[r.result_id]!.map((t, ti) => (
                                      <tr key={ti} className="border-b border-gray-100/50">
                                        <td className="py-1.5">
                                          <StatusCell ticket={t} />
                                        </td>
                                        <td className="py-1.5 text-slate-800">
                                          {t.ticket_no || t.delivery_no || '-'}
                                        </td>
                                        <td className="py-1.5 text-gray-600">
                                          {t.reg_no || '-'}
                                          {t.matched_reg_no && t.reg_no !== t.matched_reg_no && (
                                            <span className="text-purple-500 ml-1">
                                              ({t.matched_reg_no})
                                            </span>
                                          )}
                                        </td>
                                        <td className="py-1.5 text-gray-500">
                                          {t.ticket_timestamp
                                            ? new Date(t.ticket_timestamp).toLocaleString('en-ZA', {
                                                month: '2-digit',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                              })
                                            : '-'}
                                        </td>
                                        <td className="py-1.5 text-right tabular-nums">
                                          {formatWeight(t.nett_weight)}
                                        </td>
                                        <td className="py-1.5 text-gray-400">
                                          {(t.product || '-').substring(0, 20)}
                                        </td>
                                        <td
                                          className="py-1.5 text-gray-400"
                                          title={isNeedsReview(t) ? NEEDS_REVIEW_TITLE : undefined}
                                        >
                                          {matchTypeText(t) || '-'}
                                        </td>
                                        <td className="py-1.5 text-right tabular-nums">
                                          {t.weight_variance_percent != null ? (
                                            <span
                                              className={
                                                parseFloat(String(t.weight_variance_percent)) >
                                                (monitor.weight_variance_threshold || 5)
                                                  ? 'text-red-600 font-bold'
                                                  : 'text-gray-500'
                                              }
                                            >
                                              {parseFloat(
                                                String(t.weight_variance_percent)
                                              ).toFixed(1)}
                                              %
                                            </span>
                                          ) : (
                                            '-'
                                          )}
                                        </td>
                                        <td className="py-1.5 text-right tabular-nums">
                                          {t.hours_since_dispatch != null ? (
                                            <span
                                              className={
                                                t.status === 'Missing'
                                                  ? 'text-red-600 font-bold'
                                                  : 'text-gray-500'
                                              }
                                            >
                                              {formatHours(t.hours_since_dispatch)}
                                            </span>
                                          ) : (
                                            '-'
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              {filteredResults.length === 0 && (
                <div className="text-center py-10 text-xs text-gray-400">
                  No results in selected range.
                </div>
              )}
            </div>
          )}

          {/* Tickets View */}
          {viewMode === 'tickets' && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {loadingAllTickets ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-primary-500" />
                </div>
              ) : filteredTickets.length === 0 ? (
                <div className="text-center py-10 text-xs text-gray-400">
                  No tickets found
                  {ticketFilter !== 'all' ? ` with status "${statusLabel(ticketFilter)}"` : ''}.
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50 z-10">
                    <tr className="border-b border-gray-200">
                      {[
                        'Date',
                        'Status',
                        'Ticket No',
                        'Reg No',
                        'Timestamp',
                        'Weight',
                        'Product',
                        'Match',
                        'Matched Reg',
                        'Wt Var %',
                        'Hours',
                      ].map((h, i) => (
                        <th
                          key={h}
                          className={`px-2 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider ${i >= 9 ? 'text-right' : 'text-left'}`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTickets.map((t, ti) => (
                      <tr
                        key={ti}
                        className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="px-2 py-1.5 text-slate-800 font-medium whitespace-nowrap">
                          {t.result_date ? formatDate(t.result_date) : '-'}
                        </td>
                        <td className="px-2 py-1.5">
                          <StatusCell ticket={t} />
                        </td>
                        <td className="px-2 py-1.5 text-slate-800">
                          {t.ticket_no || t.delivery_no || '-'}
                        </td>
                        <td className="px-2 py-1.5 text-gray-600">{t.reg_no || '-'}</td>
                        <td className="px-2 py-1.5 text-gray-500 whitespace-nowrap">
                          {t.ticket_timestamp
                            ? new Date(t.ticket_timestamp).toLocaleString('en-ZA', {
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '-'}
                        </td>
                        <td className="px-2 py-1.5 tabular-nums">{formatWeight(t.nett_weight)}</td>
                        <td className="px-2 py-1.5 text-gray-400">
                          {(t.product || '-').substring(0, 16)}
                        </td>
                        <td
                          className="px-2 py-1.5 text-gray-400"
                          title={isNeedsReview(t) ? NEEDS_REVIEW_TITLE : undefined}
                        >
                          {matchTypeText(t) || '-'}
                        </td>
                        <td className="px-2 py-1.5 text-gray-500">{t.matched_reg_no || '-'}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {t.weight_variance_percent != null ? (
                            <span
                              className={
                                parseFloat(String(t.weight_variance_percent)) >
                                (monitor.weight_variance_threshold || 5)
                                  ? 'text-red-600 font-bold'
                                  : 'text-gray-500'
                              }
                            >
                              {parseFloat(String(t.weight_variance_percent)).toFixed(1)}%
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {t.hours_since_dispatch != null ? (
                            <span
                              className={
                                t.status === 'Missing' ? 'text-red-600 font-bold' : 'text-gray-500'
                              }
                            >
                              {formatHours(t.hours_since_dispatch)}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>

      {showAckModal && (
        <ReceiptReconAcknowledgeModal
          result={showAckModal}
          monitor={monitor}
          onClose={() => setShowAckModal(null)}
          onSaved={handleAckSaved}
        />
      )}
      {showAlertLog && (
        <ReceiptReconAlertLogModal monitor={monitor} onClose={() => setShowAlertLog(false)} />
      )}
    </div>
  );
};

export default ReceiptReconDetailModal;
